import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink, LogOut, Menu, Settings as SettingsIcon } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { useLogout } from '../../api/auth';
import { storeUrlForSlug } from '../../config';
import { initials } from '../../lib/utils';
import { NotificationBell } from './NotificationBell';

export function TopBar({ title }: { title: string }) {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const setMobileOpen = useUIStore((s) => s.setMobileSidebarOpen);
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen);
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const storeUrl = tenant ? storeUrlForSlug(tenant.slug) : null;

  return (
    <header className="sticky top-0 z-30 flex h-20 shrink-0 items-center justify-between gap-3 border-b border-border bg-white px-4 md:px-8">
      <div className="flex min-w-0 items-center gap-2.5">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-mid transition-colors hover:bg-accent-light hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent md:hidden"
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          aria-controls="cartiq-mobile-navigation"
          aria-haspopup="dialog"
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="hidden items-center gap-2.5 text-sm text-mid lg:inline-flex">
          Commerce <ChevronRight aria-hidden="true" className="h-3.5 w-3.5 text-border" />
        </span>
        <h1 className="truncate text-sm font-semibold text-primary sm:text-base">{title}</h1>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
        {storeUrl && (
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden min-h-9 items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-mid transition-colors hover:bg-accent-light hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:inline-flex"
          >
            <ExternalLink className="h-4 w-4" />
            View store
          </a>
        )}

        <NotificationBell />

        <div className="relative" ref={menuRef}>
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="cartiq-account-actions"
            aria-label="Account menu"
            className="flex min-h-10 items-center gap-2 rounded-lg p-1 sm:pr-2 transition-colors hover:bg-accent-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-xs font-semibold text-white">
              {initials(user?.name ?? 'U')}
            </span>
            <span className="hidden min-w-0 max-w-36 text-left sm:block">
              <span className="block truncate text-sm font-medium leading-tight text-primary">
                {user?.name ?? 'Account'}
              </span>
              <span className="mt-0.5 block truncate text-xs leading-tight text-mid">{tenant?.name}</span>
            </span>
            <ChevronDown aria-hidden="true" className="hidden h-3.5 w-3.5 text-mid sm:block" />
          </button>

          {open && (
            <div id="cartiq-account-actions" className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-white py-1 shadow-float animate-pop-in">
              <div className="border-b border-border px-3 py-2.5">
                <p className="truncate text-sm font-medium text-primary">{user?.name}</p>
                <p className="truncate text-xs text-mid">{user?.email}</p>
              </div>
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/settings');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-mid transition-colors hover:bg-surface hover:text-primary"
              >
                <SettingsIcon className="h-4 w-4" />
                Settings
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  logout.mutate(undefined, { onSettled: () => navigate('/login') });
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-error transition-colors hover:bg-error/5"
              >
                <LogOut className="h-4 w-4" />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
